import React from 'react';
import { SpreadsheetViewers } from '../index';
import WorkflowConstantsPanel from '../WorkflowConstantsPanel';
import WorkflowStepsDisplay from '@/components/common/WorkflowStepsDisplay';
import type { WorkflowConstants } from '../../types/WorkflowConstants';

// Phase 1: Preprocessing - Sheet selection is already complete at this point
interface PhaseOnePreprocessingProps {
  originalData: unknown[][]; // Raw uploaded file data
  preprocessedData: unknown[][]; // Phase 1 output
  isLoadingOriginal: boolean;
  isLoadingPreprocessed: boolean;
  selectedFiles: File[];
  transformError: string | null;
  onOriginalDataChange: (data: unknown[][]) => void;
  onPreprocessedDataChange: (data: unknown[][]) => void;
  onConversationClear: () => void;
  constants: WorkflowConstants | null;
  supplierName?: string;
  onConstantsChange?: (constants: WorkflowConstants) => void;
  // NEW: Workflow steps support
  workflowSteps?: Array<{
    id?: string;
    type: string;
    description: string;
    inputs?: unknown;
    outputs?: {
      columns?: string[];
      [key: string]: unknown;
    };
  }>;
  rawWorkflowSteps?: Array<{
    id?: string;
    type: string;
    description: string;
    inputs?: unknown;
    outputs?: {
      columns?: string[];
      [key: string]: unknown;
    };
  }>;
  isGeneratingWorkflow?: boolean;
  // Workflow error handling
  workflowSuccess?: boolean;
  workflowError?: string;
  failedStepId?: string;
}

const PhaseOnePreprocessing: React.FC<PhaseOnePreprocessingProps> = ({
  originalData,
  preprocessedData,
  isLoadingOriginal,
  isLoadingPreprocessed,
  selectedFiles,
  transformError,
  onOriginalDataChange,
  onPreprocessedDataChange: _onPreprocessedDataChange,
  onConversationClear,
  constants,
  supplierName,
  onConstantsChange,
  workflowSteps,
  rawWorkflowSteps,
  isGeneratingWorkflow = false,
  workflowError,
  failedStepId,
}) => {
  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Data Preprocessing</h2>

      <WorkflowConstantsPanel
        constants={constants}
        supplierName={supplierName}
        phase={1}
        onConstantsChange={onConstantsChange}
      />

      <p className="text-sm text-gray-600 mb-6">
        The goal of this step is to clean and prepare the data for
        transformation by standardizing headers and filling down implied values.
      </p>

      {/* Workflow Steps Display */}
      {(isGeneratingWorkflow || workflowSteps) && (
        <WorkflowStepsDisplay
          workflowSteps={workflowSteps}
          rawWorkflowSteps={rawWorkflowSteps}
          isGenerating={isGeneratingWorkflow}
          title="Preprocessing Steps"
          tooltip="These steps clean and prepare your data by extracting table bounds, constructing headers, filling down values, and performing intelligent reverse pivot operations for wide columns."
          phase="preprocessing"
          className="mb-6"
          workflowError={workflowError}
          failedStepId={failedStepId}
        />
      )}

      {originalData.length > 0 && (
        <SpreadsheetViewers
          title="Data Preprocessing"
          originalTitle="Original Data"
          transformedTitle="Preprocessed Data"
          originalData={originalData}
          transformedData={preprocessedData}
          isLoadingOriginal={isLoadingOriginal}
          isLoadingTransformed={isLoadingPreprocessed}
          transformError={transformError}
          workbook={null} // No sheet selection in preprocessing phase
          selectedSheet={undefined} // No sheet selection in preprocessing phase
          onSheetChange={() => {}} // No sheet selection in preprocessing phase
          onConversationClear={onConversationClear}
          onOriginalDataChange={onOriginalDataChange}
          filename={selectedFiles[0]?.name || 'spreadsheet'}
        />
      )}
    </div>
  );
};

export default PhaseOnePreprocessing;
